/**
 * Apartsales — Route `/$country/$region/$city/$district/$developer/$complex/$type/$unit`
 *
 * Новый канонический 8-сегментный URL для страницы юнита.
 * Пример: /russia/krasnodar-krai/sochi/adler/ug-development/morskoy-briz/apartment/2k-65m-a-08-101
 *
 * См. docs/apartsales/07-URL-ARCHITECTURE-v1.md §3.
 *
 * Для городов федерального значения (Москва, СПб, Севастополь) URL имеет
 * 7 сегментов с пропущенным регионом — это **отдельный** route
 * `$country.$city.$district.$developer.$complex.$type.$unit.tsx`
 * (добавляется позже).
 *
 * Этот route работает только после применения миграции 0019, которая
 * заполняет projects.country_id/region_id/city_id/district_id. До 0019 —
 * все запросы возвращают 404 (это нормально, новый URL не запущен публично).
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';

import { UnitDetailPage } from '~/components/UnitDetailPage';
import { fetchUnitCard } from '~/lib/unit-card-api';
import { unitCardToProperty } from '~/lib/unit-card-adapter';
import { resolveSlugPathV2ToUnitId } from '~/lib/slug-routing-v2';

export const Route = createFileRoute(
  '/$country/$region/$city/$district/$developer/$complex/$type/$unit',
)({
  component: UnitBySlugV2Screen,
});

function UnitBySlugV2Screen() {
  const { country, region, city, district, developer, complex, type, unit } =
    Route.useParams();

  const { data: unitId, isLoading: resolving, error: resolveError } = useQuery({
    queryKey: [
      'unit-slug-v2',
      country, region, city, district, developer, complex, type, unit,
    ],
    queryFn: () =>
      resolveSlugPathV2ToUnitId({
        country, region, city, district, developer, complex, type, unit,
      }),
    staleTime: 5 * 60_000,
  });

  const { data, isLoading: loadingCard, error: cardError } = useQuery({
    queryKey: ['unit-card', unitId],
    queryFn: () =>
      unitId ? fetchUnitCard(unitId) : Promise.reject(new Error('No unitId')),
    enabled: !!unitId,
    staleTime: 60_000,
  });

  const isLoading = resolving || (unitId && loadingCard);
  const error = resolveError || cardError;

  if (isLoading) {
    return (
      <div className="min-h-screen px-4 py-6">
        <div className="aspect-[4/5] w-full animate-pulse rounded-[20px] bg-bg-3" />
        <div className="mt-6 space-y-3">
          <div className="h-4 w-1/3 animate-pulse rounded-full bg-bg-3" />
          <div className="h-3 w-2/3 animate-pulse rounded-full bg-bg-3" />
          <div className="h-3 w-1/2 animate-pulse rounded-full bg-bg-3" />
        </div>
      </div>
    );
  }

  if (!unitId && !resolving) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center">
        <div className="text-[22px] font-bold text-label">Юнит не найден</div>
        <div className="mt-2 max-w-[280px] text-[14px] text-label-2">
          Возможно, юнит снят с продажи или ссылка устарела.
        </div>
        <Link
          to="/"
          className="mt-6 rounded-full bg-brand-blue px-5 py-2.5 text-[14px] font-semibold text-white transition-transform active:scale-95"
        >
          На главную
        </Link>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center">
        <div className="text-[20px] font-bold text-label">
          Не удалось загрузить юнит
        </div>
        <div className="mt-2 max-w-[280px] text-[14px] text-label-2">
          {error ? (error as Error).message : 'Попробуйте обновить страницу.'}
        </div>
        <Link
          to="/"
          className="mt-6 rounded-full bg-brand-blue px-5 py-2.5 text-[14px] font-semibold text-white transition-transform active:scale-95"
        >
          На главную
        </Link>
      </div>
    );
  }

  const property = unitCardToProperty(data);
  return <UnitDetailPage property={property} />;
}
